import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

const INDUSTRIES = [
  'IT Services', 'Real Estate', 'Law', 'Accounting', 'Construction',
  'Manufacturing', 'Restaurants', 'Retail', 'Gyms', 'Insurance',
  'Staffing', 'Property Management', 'HVAC', 'Landscaping', 'Bookkeeping',
  'Healthcare', 'Automotive', 'Education', 'Marketing', 'Other'
];

const BUYING_SIGNALS = [
  'hiring IT', 'IT manager', 'IT director', 'expanding', 'new location',
  'cybersecurity', 'data breach', 'ransomware', 'looking for IT',
  'switching IT', 'IT problems', 'just raised', 'funding'
];

const TONES = ['Professional', 'Casual', 'Referral-style', 'Consultative'];

function ProspectingSetup() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    company_name: '', company_industry: '', problem_statement: '', service_area: '',
    signature_name: '', from_email: '',
    target_industries: [], company_size_min: 5, company_size_max: 50,
    target_cities: '', buying_signals: [], excluded_companies: '',
    daily_draft_count: 10, schedule_days: ['mon','tue','wed','thu','fri'],
    schedule_time: '08:00', email_tone: 'professional', followup_sequence: 3,
    auto_pause_threshold: 30, is_active: false,
  });

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      const res = await fetch('/api/prospecting/config', { credentials: 'include' });
      const data = await res.json();
      if (data && data.id) {
        setConfig(prev => ({
          ...prev,
          ...data,
          target_cities: Array.isArray(data.target_cities) ? data.target_cities.join(', ') : data.target_cities || '',
          excluded_companies: Array.isArray(data.excluded_companies) ? data.excluded_companies.join(', ') : data.excluded_companies || '',
        }));
      }
    } catch (e) { console.error('Failed to load config:', e); }
    finally { setLoading(false); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const body = {
        ...config,
        target_cities: config.target_cities.split(',').map(s => s.trim()).filter(Boolean),
        excluded_companies: config.excluded_companies.split(',').map(s => s.trim()).filter(Boolean),
      };
      const res = await fetch('/api/prospecting/config', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      toast.success('Configuration saved');
      return data;
    } catch (e) { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const activate = async () => {
    await saveConfig();
    try {
      const res = await fetch('/api/prospecting/activate', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.error) { toast.error(data.error); return; }
      setConfig(prev => ({ ...prev, is_active: true }));
      toast.success('Prospecting activated! Drafts will be generated daily.');
    } catch (e) { toast.error('Failed to activate'); }
  };

  const deactivate = async () => {
    try {
      await fetch('/api/prospecting/deactivate', { method: 'POST', credentials: 'include' });
      setConfig(prev => ({ ...prev, is_active: false }));
      toast.success('Prospecting paused');
    } catch (e) { toast.error('Failed to pause'); }
  };

  const triggerNow = async () => {
    toast.loading('Triggering prospecting run... (may take a few minutes)');
    try {
      const res = await fetch('/api/prospecting/trigger', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      toast.dismiss();
      if (data.error) { toast.error(data.error); return; }
      if (data.skipped) { toast(`Skipped: ${data.reason} (${data.drafts || 0} drafts pending)`); return; }
      toast.success(`Prospecting done! Check pipeline for new drafts.`);
    } catch (e) { toast.error('Failed to trigger'); }
  };

  const toggleIndustry = (ind) => {
    setConfig(prev => ({
      ...prev,
      target_industries: prev.target_industries.includes(ind)
        ? prev.target_industries.filter(i => i !== ind)
        : [...prev.target_industries, ind],
    }));
  };

  const toggleSignal = (sig) => {
    setConfig(prev => ({
      ...prev,
      buying_signals: prev.buying_signals.includes(sig)
        ? prev.buying_signals.filter(s => s !== sig)
        : [...prev.buying_signals, sig],
    }));
  };

  const toggleDay = (day) => {
    setConfig(prev => ({
      ...prev,
      schedule_days: prev.schedule_days.includes(day)
        ? prev.schedule_days.filter(d => d !== day)
        : [...prev.schedule_days, day],
    }));
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div></div>;
  }

  const days = [
    { id: 'mon', label: 'Mon' }, { id: 'tue', label: 'Tue' },
    { id: 'wed', label: 'Wed' }, { id: 'thu', label: 'Thu' },
    { id: 'fri', label: 'Fri' }, { id: 'sat', label: 'Sat' }, { id: 'sun', label: 'Sun' },
  ];

  return (
    <div className="p-6 max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">🎯 Prospecting Setup</h1>
        {config.is_active && (
          <span className="px-3 py-1 text-sm bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded-full">● Active</span>
        )}
      </div>

      {/* Status banner */}
      {config.is_active ? (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6 flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-300">✅ Prospecting is active</p>
            <p className="text-xs text-green-600 dark:text-green-400 mt-1">
              {config.daily_draft_count} drafts/day · {config.schedule_days.length} days/week · {config.schedule_time} UTC
            </p>
          </div>
          <div className="flex gap-2">
            <button onClick={triggerNow} className="px-3 py-1.5 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600">Run Now</button>
            <button onClick={deactivate} className="px-3 py-1.5 text-sm bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300 rounded-lg hover:bg-red-200">Pause</button>
          </div>
        </div>
      ) : null}

      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-8">
        {[1, 2, 3, 4].map(n => (
          <React.Fragment key={n}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
              step >= n ? 'bg-blue-500 text-white' : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
            }`}>{n}</div>
            {n < 4 && <div className={`flex-1 h-1 rounded ${step > n ? 'bg-blue-500' : 'bg-gray-200 dark:bg-gray-700'}`}></div>}
          </React.Fragment>
        ))}
      </div>

      {/* Step 1: About Your Business */}
      {step === 1 && (
        <div className="space-y-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">About Your Business</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Tell us about your company so we can write emails in your voice.</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name *</label>
            <input type="text" value={config.company_name} onChange={e => setConfig({...config, company_name: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your Industry *</label>
            <select value={config.company_industry} onChange={e => setConfig({...config, company_industry: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
              <option value="">Select...</option>
              {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">What problem do you solve? *</label>
            <textarea value={config.problem_statement} onChange={e => setConfig({...config, problem_statement: e.target.value})} rows={2} maxLength={200}
              placeholder="e.g. Managed IT, cybersecurity, cloud services for small businesses"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Area *</label>
            <input type="text" value={config.service_area} onChange={e => setConfig({...config, service_area: e.target.value})}
              placeholder="e.g. Austin / Round Rock / Cedar Park TX"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Signature Name *</label>
              <input type="text" value={config.signature_name} onChange={e => setConfig({...config, signature_name: e.target.value})}
                placeholder="e.g. Rebecca Helfer"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">From Email *</label>
              <input type="email" value={config.from_email} onChange={e => setConfig({...config, from_email: e.target.value})}
                placeholder="rebecca@networx.tech"
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
            </div>
          </div>

          <div className="flex justify-end">
            <button onClick={() => setStep(2)} disabled={!config.company_name || !config.company_industry || !config.problem_statement}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">Next →</button>
          </div>
        </div>
      )}

      {/* Step 2: Target Customers */}
      {step === 2 && (
        <div className="space-y-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Target Customers</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Who are you looking for? We'll find businesses that match.</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Target Industries (select all that apply) *</label>
            <div className="flex flex-wrap gap-2">
              {INDUSTRIES.map(ind => (
                <button key={ind} onClick={() => toggleIndustry(ind)}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
                    config.target_industries.includes(ind)
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-blue-400'
                  }`}>{ind}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Company Size Range: {config.company_size_min} - {config.company_size_max} employees</label>
            <div className="flex items-center gap-4">
              <input type="range" min="1" max="50" value={config.company_size_min} onChange={e => setConfig({...config, company_size_min: parseInt(e.target.value)})}
                className="flex-1" />
              <span className="text-sm text-gray-500">{config.company_size_min}</span>
              <span className="text-gray-400">to</span>
              <input type="range" min="50" max="500" value={config.company_size_max} onChange={e => setConfig({...config, company_size_max: parseInt(e.target.value)})}
                className="flex-1" />
              <span className="text-sm text-gray-500">{config.company_size_max}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Cities / Regions (comma-separated) *</label>
            <input type="text" value={config.target_cities} onChange={e => setConfig({...config, target_cities: e.target.value})}
              placeholder="Austin, Cedar Park, Round Rock"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Buying Signals (optional)</label>
            <div className="flex flex-wrap gap-2">
              {BUYING_SIGNALS.map(sig => (
                <button key={sig} onClick={() => toggleSignal(sig)}
                  className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${
                    config.buying_signals.includes(sig)
                      ? 'bg-purple-500 text-white border-purple-500'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 dark:border-gray-600 hover:border-purple-400'
                  }`}>{sig}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Companies to Exclude (comma-separated, optional)</label>
            <textarea value={config.excluded_companies} onChange={e => setConfig({...config, excluded_companies: e.target.value})} rows={2}
              placeholder="Existing Client A, Existing Client B"
              className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300">← Back</button>
            <button onClick={() => setStep(3)} disabled={config.target_industries.length === 0 || !config.target_cities}
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed">Next →</button>
          </div>
        </div>
      )}

      {/* Step 3: Email Preferences */}
      {step === 3 && (
        <div className="space-y-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Email Preferences</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">How many drafts, what tone, and when to generate them.</p>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Daily Draft Count: {config.daily_draft_count}</label>
            <input type="range" min="5" max="20" value={config.daily_draft_count} onChange={e => setConfig({...config, daily_draft_count: parseInt(e.target.value)})}
              className="w-full" />
            <div className="flex justify-between text-xs text-gray-400 mt-1"><span>5 (quality)</span><span>20 (volume)</span></div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Schedule (which days)</label>
            <div className="flex gap-2">
              {days.map(d => (
                <button key={d.id} onClick={() => toggleDay(d.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium border ${
                    config.schedule_days.includes(d.id)
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white dark:bg-gray-800 text-gray-500 border-gray-300 dark:border-gray-600'
                  }`}>{d.label}</button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Run Time (UTC)</label>
              <input type="time" value={config.schedule_time} onChange={e => setConfig({...config, schedule_time: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Tone</label>
              <select value={config.email_tone} onChange={e => setConfig({...config, email_tone: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100">
                {TONES.map(t => <option key={t.toLowerCase()} value={t.toLowerCase()}>{t}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Follow-up Sequence</label>
            <div className="flex items-center gap-3">
              <button onClick={() => setConfig({...config, followup_sequence: config.followup_sequence === 3 ? 1 : 3})}
                className={`px-4 py-2 rounded-lg text-sm border ${config.followup_sequence === 3 ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'}`}>
                3-step (Email + Follow-up + LinkedIn)
              </button>
              <button onClick={() => setConfig({...config, followup_sequence: 1})}
                className={`px-4 py-2 rounded-lg text-sm border ${config.followup_sequence === 1 ? 'bg-blue-500 text-white border-blue-500' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'}`}>
                Single email only
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Auto-Pause Threshold: {config.auto_pause_threshold} unreviewed drafts</label>
            <input type="number" min="10" max="100" value={config.auto_pause_threshold} onChange={e => setConfig({...config, auto_pause_threshold: parseInt(e.target.value) || 30})}
              className="w-32 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100" />
            <p className="text-xs text-gray-400 mt-1">Prospecting pauses when you have this many unreviewed drafts.</p>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(2)} className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300">← Back</button>
            <button onClick={() => setStep(4)} className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">Review →</button>
          </div>
        </div>
      )}

      {/* Step 4: Review & Activate */}
      {step === 4 && (
        <div className="space-y-5">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review & Activate</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Review your settings, then start prospecting.</p>

          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-5 space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500 dark:text-gray-400">Company:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.company_name || '—'}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Industry:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.company_industry || '—'}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Service Area:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.service_area || '—'}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Signature:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.signature_name || '—'}</span></div>
              <div className="col-span-2"><span className="text-gray-500 dark:text-gray-400">Problem:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.problem_statement || '—'}</span></div>
            </div>
            <hr className="border-gray-200 dark:border-gray-700" />
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500 dark:text-gray-400">Target Industries:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.target_industries.join(', ') || '—'}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Size Range:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.company_size_min}-{config.company_size_max}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Cities:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.target_cities || '—'}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Buying Signals:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.buying_signals.length || 0} selected</span></div>
            </div>
            <hr className="border-gray-200 dark:border-gray-700" />
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-500 dark:text-gray-400">Drafts/day:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.daily_draft_count}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Schedule:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.schedule_days.join(', ').toUpperCase()} @ {config.schedule_time} UTC</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Tone:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.email_tone}</span></div>
              <div><span className="text-gray-500 dark:text-gray-400">Auto-pause:</span> <span className="font-medium text-gray-900 dark:text-gray-100">{config.auto_pause_threshold} drafts</span></div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              💡 <strong>Before activating:</strong> Make sure your email is connected in Settings → Email Integration.
              Drafts will appear in your Pipeline tab for review. You can edit, approve, or reject each one.
            </p>
          </div>

          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300">← Back</button>
            <div className="flex gap-2">
              <button onClick={saveConfig} disabled={saving} className="px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300">
                {saving ? 'Saving...' : 'Save Draft'}
              </button>
              <button onClick={activate} disabled={saving}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50">
                {config.is_active ? 'Update & Save' : 'Start Prospecting →'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProspectingSetup;